import { axiosInstance } from './axiosInstance'
import { getAccumulatedBom } from '../../../helpers/getAccumulatedBom'
import { type APIConfig } from '../apiTypes'
import type * as DynamakerContract from '../../../integration/DynamakerContract'

const axios = axiosInstance()

/*
  The shape of the response by article status, verified against the test environment 2026-08-26.

  Every article we send gets a row in `articles` -- nothing is silently left out.
  `errors` is only present when at least one article cannot be bought.

  | Article status | The row in `articles`                  | `errors`                                      |
  |----------------|----------------------------------------|-----------------------------------------------|
  | buyable        | complete, IsAvailableForPurchase: true  | the key is absent altogether                  |
  | not buyable    | complete, the price is part of summary  | item_not_added_to_cart, "(NotBuyable)"        |
  | unknown Sku    | Sku and Title null, Price 0             | item_not_added_to_cart, "(ProductIdNotFound)" |

  summary.IsAvailableForPurchase turns false as soon as any row is, and the article
  number can only be read out of the error's Message -- there is no field of its own.

  The table describes a flat payload. The backend collapses BuildingKit sections into a
  fictitious article FSA001, and a broken component in such a section then shows up only
  in errors, not in articles. That case is untested.

  The backend caches article data for 240 minutes, negative lookups included. Whether
  that applies to this call as well is unconfirmed -- see postArticleinfolist.ts.
*/

export interface DsaPricehelperArticle {
  Sku: string | null
  Title: string | null
  TitleLong: string | null
  DescriptionShort: string | null
  IsAvailableForPurchase: boolean
  ArticleUrl: string
  Price: number
  FormatedPrice: string
  OrdinaryPrice: number
  FormatedOrdinaryPrice: string
  Quantity: number
  Attributes: unknown
  Documents: unknown
}

export interface DsaPricehelperError {
  Code: string // known code: 'item_not_added_to_cart'
  Message: string
}

export interface DsaPricehelperSummary {
  Price: number
  FormatedPrice: string
  OrdinaryPrice: number
  FormatedOrdinaryPrice: string
  ProductDiscount: number
  FormatedProductDiscount: string
  DeliveryInformation: string
  DeliveryStatus: string // observed value: 'UnknownAvailability'
  IsAvailableForPurchase: boolean
  Discounts: unknown[]
}

export interface DsaPricehelperResponse {
  errors?: DsaPricehelperError[]
  articles: DsaPricehelperArticle[]
  summary: DsaPricehelperSummary
}

export const postDsaPricehelper = async <T = any>(args: {
  apiConfigs: APIConfig
  data: {
    bom: DynamakerContract.BOM
    configId: string
    content: Record<PropertyKey, any>
    meta: DynamakerContract.Metadata
  }
  onSuccess: (response: { data: DsaPricehelperResponse }) => T
}): Promise<T> => {
  const {
    onSuccess,
    apiConfigs: { scensumBaseUrl },
    data: { bom, configId, content, meta },
  } = args

  const accumulatedBom = getAccumulatedBom(bom)

  const data = {
    ConfigurationType: 'mok',
    ConfigurationId: configId,
    Meta: {
      Title: '(intentionally left blank)',
      TitleLong: '(intentionally left blank)',
    },
    ConfigurationSections: accumulatedBom.map((section: any) => ({
      Type: section.type,
      Position: section.position,
      OwnerPosition: section.ownerPosition ?? null,
      Meta: {
        Title: content?.bom?.[section.type]?.title ?? '(intentionally left blank)',
        TitleLong: content?.bom?.[section.type]?.titleLong
          ? `${content?.bom?.[section.type]?.titleLong} ${meta.buildingArea.toString().replace('.', ',')} m²`
          : '(intentionally left blank)',
      },
      // TODO ts
      Articles: section.articles.map((item: any) => ({
        Sku: item.erpItemNumber,
        Quantity: item.quantity,
        ...('configData' in item &&
          item.configData && {
            Type: 'configProduct',
            // only works for single config product in configResult, equivalent to current implementation in lfc-frontend
            PricingInfo: item.configData.configResult?.configProducts?.[0]?.pricingInfo,
          }),
      })),
    })),
  }

  return await axios<DsaPricehelperResponse>({
    method: 'post',
    url: `${scensumBaseUrl}/api/v1/dsapricehelper`,
    data,
  })
    .then((response) => onSuccess(response))
    .catch((error) => {
      console.error(error)
      throw new Error(JSON.stringify(error))
    })
}
